
import {
  defineComponent, reactive, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import SpmOverlayPanel from '@/components/spm-primevue/SpmOverlayPanel.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import BaseTextInput from '@/components/fields/BaseTextInput.vue';
import Checkbox from 'primevue/checkbox';
import { required } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { ListType } from '@/types';
import { showToastError, showToastSuccess } from '@/helpers';
import { nestCall, NestApiType } from '@/composables/nestApi';

export default defineComponent({
  name: 'ListCreatePanelV2',

  components: {
    SpmOverlayPanel,
    SpmButton,
    BaseTextInput,
    Checkbox,
  },

  setup() {
    const { t } = useI18n();
    const router = useRouter();

    const loading = ref(false);
    const closePanel = ref(false);
    const formValidation: any = ref<Record<string, unknown>>({});

    // Form state
    const listName = ref('');
    const selectedType = ref<'list' | 'segment'>('list');
    const importAfterCreate = ref(true);

    const handleCreate = async () => {
      // Validate form
      const data = reactive({
        listName: listName.value,
      });

      const rules = {
        listName: { required },
      };

      const v$ = useVuelidate(rules, data);
      const success = await v$.value.$validate();

      if (!success) {
        formValidation.value = v$;
        return;
      }

      loading.value = true;

      try {
        // Create the list via NestJS API
        const payload = {
          name: listName.value,
          type: selectedType.value === 'segment' ? ListType.SEGMENT : ListType.LIST,
        };

        const response = await nestCall(
          'post',
          NestApiType.CLIENT_API,
          '/lists',
          {},
          '',
          payload,
          true,
        );

        if (!response || response.statusCode) {
          const errorMessage = response?.message || t('myLists.manage.createPanelV2.error');
          await showToastError(errorMessage);
          loading.value = false;
          return;
        }

        // Get the new list ID from response
        const newListId = response.id_shop_list;

        // Save values before reset
        const shouldImport = selectedType.value === 'list' && importAfterCreate.value;

        await showToastSuccess(t('myLists.manage.createPanelV2.success'));

        // Close panel
        closePanel.value = true;

        // Reset form
        listName.value = '';
        selectedType.value = 'list';
        importAfterCreate.value = true;
        formValidation.value = {};

        // Redirect to contacts management page with optional import query param
        await router.push({
          name: 'lists.manage-contacts',
          params: { id: newListId },
          query: shouldImport ? { openImport: 'true' } : undefined,
        });
      } catch (error) {
        console.error('Error creating list:', error);
        await showToastError(t('myLists.manage.createPanelV2.error'));
      } finally {
        loading.value = false;
      }
    };

    return {
      t,
      loading,
      closePanel,
      formValidation,
      listName,
      selectedType,
      importAfterCreate,
      handleCreate,
    };
  },
});
